import { Literal } from "components/Literal";
import { OpenCloseIcon } from "components/OpenCloseIcon";
import { FontWeight } from "components/theme";
import {
  useAppointmentsQuery,
  useAppointmentUpdateInterval
} from "core/services/booking";
import { LayoutConstants } from "layout/LayoutConstants";
import { PropsWithChildren } from "react";
import { Link, useMatch } from "react-router-dom";
import { useToggle } from "react-use";
import urlJoin from "url-join";

import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import PeopleIcon from "@mui/icons-material/People";
import PersonIcon from "@mui/icons-material/Person";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import {
  alpha,
  Badge,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  styled,
  useTheme
} from "@mui/material";

const StyledListItem = styled(ListItemButton)(({ theme, selected }) => ({
  borderLeft: "4px solid transparent",
  padding: theme.spacing(2),

  display: "flex",

  "&:hover": {
    backgroundColor: alpha(theme.palette.secondary.light, 0.3)
  },

  "& .MuiListItemIcon-root:first-of-type": {
    minWidth: `calc(${
      LayoutConstants.leftMenu.width.closed
    }px - 4px - ${theme.spacing(2)})`
  },

  "& .MuiTypography-root": {
    flex: "1 1 1px",
    marginRight: theme.spacing(2)
  },

  ...(selected && {
    borderLeftColor: "orange",
    backgroundColor: theme.palette.secondary.light,

    "&:hover": {
      backgroundColor: theme.palette.secondary.light
    },

    "& svg": {
      color: theme.palette.secondary.main
    }
  })
}));

interface ListItemLinkProps {
  label: string;
  /**
   * The route to go to when the item is clicked.
   */
  to: string;
  /**
   * The route to match when determining if the link is selected. If omitted, the `to` route will be used.
   */
  matchRoute?: string;
  open?: boolean;
}

const ListItemLink = ({
  to,
  matchRoute,
  label,
  children
}: PropsWithChildren<ListItemLinkProps>) => {
  const selected = !!useMatch(matchRoute ?? urlJoin(to, "*"));

  // We can't pass the `component` prop to a styled `ListItemButton` without TS complaining (see: https://github.com/mui-org/material-ui/issues/14971#issuecomment-566450079).
  // So instead we're creating an anonymous type as a crappy work-around.
  const props = {
    component: Link,
    to,
    selected
  };

  return (
    <StyledListItem {...props}>
      <ListItemIcon>{children}</ListItemIcon>
      <Literal
        variant="body1"
        component="span"
        fontWeight={selected ? FontWeight.medium : FontWeight.regular}
      >
        {label}
      </Literal>
    </StyledListItem>
  );
};

interface ListItemGroupProps {
  label: string;
}

const ListItemGroup = ({
  label,
  children
}: PropsWithChildren<ListItemGroupProps>) => {
  const [expanded, toggleExpanded] = useToggle(true);

  return (
    <>
      <StyledListItem onClick={toggleExpanded}>
        <ListItemIcon>
          <SettingsOutlinedIcon />
        </ListItemIcon>
        <Literal variant="body1" component="span">
          {label}
        </Literal>
        <OpenCloseIcon open={expanded} icon={KeyboardArrowDownIcon} />
      </StyledListItem>
      {expanded && children}
    </>
  );
};

export const MenuItems = () => {
  const { data: appointmentsQuery } = useAppointmentsQuery({
    onOrAfter: useAppointmentUpdateInterval()
  });

  const theme = useTheme();
  return (
    <List sx={{ padding: "0" }}>
      <Divider />
      <ListItemLink label="Dashboard" to="dashboard">
        <DashboardOutlinedIcon />
      </ListItemLink>

      <ListItemLink label="Appointments" to="appointments">
        <Badge
          badgeContent={appointmentsQuery?.length}
          color="secondary"
          sx={{ ".MuiBadge-badge": { color: theme.palette.common.white } }}
        >
          <CalendarTodayIcon />
        </Badge>
      </ListItemLink>

      <ListItemGroup label="Manage Account">
        <ListItemLink
          label="My Profile"
          to="profile/details"
          matchRoute="profile/*"
        >
          <PersonIcon />
        </ListItemLink>
        <ListItemLink label="Family Members" to="family">
          <PeopleIcon />
        </ListItemLink>
      </ListItemGroup>
    </List>
  );
};
